import { Fragment, Component, Host, Prop, State, h } from '@stencil/core';

const API_URL = process.env.NODE_ENV === 'production' ? '/wp-json/formhandler/v1/questionnaire?id=' : 'http://localhost:8080/wp-json/formhandler/v1/questionnaire?id=';

interface Question {
  id: string;
  show: string;
  name: string;
  question: string;
  dependency?: {
    id: string;
    value: string;
  };
  dependentantQuestions?: Question[];
  type: 'radio' | 'select' | 'text' | 'number' | 'range';
  options?: { value: string; label: string }[];
  placeholder?: string;
  rangeOptions?: {
    min: number;
    max: number;
    steps: number;
  };
}

const ANY_MATCHER = '*any*';

@Component({
  tag: 'company-questionnaire',
  styleUrl: 'company-questionnaire.scss',
  shadow: false,
})
export class CompanyQuestionnaire {
  @State() questions: Question[] = [];

  @State() questionsToShow: Map<Question['id'], Question> = new Map();

  @State() answers: { [key: string]: string } = {};

  @Prop() label: string = 'Company:';

  @Prop() required: boolean = true;

  @Prop() questionnaire: string;

  componentDidLoad() {
    fetch(`${API_URL}${this.questionnaire}`)
      .then(response => response.json())
      .then((data: Question[]) => {
        this.questions = data;
        this.initialQuestionsToShow(data);
      })
      .catch(error => {
        console.error(error);
      });
  }

  initialQuestionsToShow(questions: Question[]) {
    // loop through the questions array and find all questions without any dependencies, starting from the first question
    for (let i = 0; i < questions.length; i++) {
      if (questions[i].dependency.id === '' && questions[i].dependency.value === '') {
        this.questionsToShow.set(questions[i].id, questions[i]);
      } else {
        break;
      }
    }
  }

  handleChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const questionId = target.getAttribute('id');
    const currentQuestion = this.questions.find(question => question.id === questionId);

    this.answers[questionId] = target.value;

    this.checkDependencies(currentQuestion);
  }

  getDependentQuestions(currentQuestion: Question) {
    return this.questions.filter(question => {
      if (question.dependency) {
        return question.dependency.id === currentQuestion.id;
      }
    });
  }

  checkDependencies(currentQuestion: Question) {
    const dependentQuestions = this.getDependentQuestions(currentQuestion);

    dependentQuestions.forEach(question => {
      const dependencyMet = this.dependencyMet(question.dependency);

      if (dependencyMet) {
        this.showQuestion(question);
      } else {
        this.hideQuestion(question);
      }
    });
  }

  showQuestion(question: Question) {
    const newMap = new Map(this.questionsToShow);
    newMap.set(question.id, question);

    this.questionsToShow = newMap;
  }

  hideQuestion(question: Question) {
    const newMap = new Map(this.questionsToShow);
    newMap.delete(question.id);
    this.answers[question.id] = '';

    this.questionsToShow = newMap;

    this.getDependentQuestions(question).forEach(q => {
      this.hideQuestion(q);
    });
  }

  dependencyMet(dependency: Question['dependency']) {
    const dependencyValue = dependency.value;

    const matchAny = dependencyValue === ANY_MATCHER;

    return matchAny ? this.answers[dependency.id] != '' : this.answers[dependency.id] === dependencyValue;
  }

  render() {
    return (
      <Host>
        {Array.from(this.questionsToShow.values()).map(question => {
          switch (question.type) {
            case 'radio':
              return (
                <Fragment>
                  <label htmlFor={question.name || question.id}>{question.question}</label>
                  {question.options.map(option => (
                    <label>
                      <input
                        id={question.id}
                        type="radio"
                        name={question.name || question.id}
                        value={option.value}
                        checked={this.answers[question.id] === option.value}
                        onChange={event => this.handleChange(event)}
                        required={this.required}
                      />
                      {option.label}
                    </label>
                  ))}
                </Fragment>
              );
            case 'select':
              return (
                <Fragment>
                  <label htmlFor={question.name || question.id}>{question.question}</label>
                  <select id={question.id} name={question.name} onChange={event => this.handleChange(event)} required={this.required}>
                    <option value="" disabled selected>
                      {question.placeholder || 'Please choose'}
                    </option>
                    {question.options.map(option => (
                      <option value={option.value} selected={this.answers[question.id] === option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Fragment>
              );
            case 'text':
              return (
                <Fragment>
                  <label htmlFor={question.name || question.id}>{question.question}</label>
                  <input
                    id={question.id}
                    type="text"
                    name={question.name}
                    value={this.answers[question.id]}
                    placeholder={question.placeholder}
                    onInput={event => this.handleChange(event)}
                    required={this.required}
                  />
                </Fragment>
              );
            case 'number':
              return (
                <Fragment>
                  <label htmlFor={question.name || question.id}>{question.question}</label>
                  <input
                    id={question.id}
                    type="number"
                    name={question.name}
                    value={this.answers[question.id]}
                    placeholder={question.placeholder}
                    onInput={event => this.handleChange(event)}
                    required={this.required}
                  />
                </Fragment>
              );

            case 'range':
              return (
                <Fragment>
                  <label htmlFor={question.name || question.id}>{question.question}</label>
                  <div class="company-questionnaire__range">
                    <input
                      id={question.id}
                      type="range"
                      name={question.name}
                      value={this.answers[question.id]}
                      defaultValue={question.rangeOptions.min.toString()}
                      min={question.rangeOptions.min}
                      max={question.rangeOptions.max}
                      step={question.rangeOptions.steps}
                      onInput={event => {
                        this.handleChange(event);
                        const nextSibling = (event.target as HTMLInputElement).nextElementSibling;
                        nextSibling.textContent = (event.target as HTMLInputElement).value;
                      }}
                      required={this.required}
                    />
                    <span>{this.answers[question.id]}</span>
                  </div>
                </Fragment>
              );
          }
        })}
      </Host>
    );
  }
}
